import { useSidebar } from '@/components/ui/sidebar'
import { cn } from '@/lib/utils'

interface MobileNavProps {
    pageTitle?: string
    isTopNavVisible?: boolean
    // Fallback props for when not inside SidebarProvider
    isOpen?: boolean
    onToggle?: () => void
}

// Custom Drawer Icons
const DrawerInactive = ({ className }: { className?: string }) => (
    <svg
        viewBox="0 0 32 32"
        fill="none"
        className={className}
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect
            x="6.33333"
            y="9.00008"
            width="19.3333"
            height="2.66667"
            fill="#FEF5F5"
            stroke="#333333"
            strokeWidth="0.666667"
        />
        <rect
            x="6.33333"
            y="15.0001"
            width="19.3333"
            height="2.66667"
            fill="#FEF5F5"
            stroke="#333333"
            strokeWidth="0.666667"
        />
        <rect
            x="6.33333"
            y="20.3333"
            width="19.3333"
            height="2.66667"
            fill="#FEF5F5"
            stroke="#333333"
            strokeWidth="0.666667"
        />
        <path
            d="M7.35156 7.69389V5.3335"
            stroke="#EF3837"
            strokeWidth="0.666667"
        />
        <path
            d="M4.70823 9.04324L2.66406 7.86304"
            stroke="#EF3837"
            strokeWidth="0.666667"
        />
        <path
            d="M5.82864 7.92185L4.64844 5.87769"
            stroke="#EF3837"
            strokeWidth="0.666667"
        />
    </svg>
)

const DrawerActive = ({ className }: { className?: string }) => (
    <svg
        viewBox="0 0 32 32"
        fill="none"
        className={className}
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect
            x="7.80734"
            y="21.7515"
            width="20.6667"
            height="2.66667"
            transform="rotate(-45 7.80734 21.7515)"
            fill="#FEF5F5"
            stroke="#333333"
            strokeWidth="0.666667"
        />
        <rect
            x="9.69141"
            y="7.13791"
            width="20.6667"
            height="2.66667"
            transform="rotate(45 9.69141 7.13791)"
            fill="#FEF5F5"
            stroke="#333333"
            strokeWidth="0.666667"
        />
        <path
            d="M16.0533 17.2758L14.1639 15.3885L16.0544 13.5125L17.9408 15.3893L16.0533 17.2758Z"
            fill="#EF3837"
            stroke="#333333"
            strokeWidth="0.666667"
        />
    </svg>
)

const MobileNav = ({
    pageTitle,
    isTopNavVisible,
    isOpen: propIsOpen,
    onToggle: propOnToggle,
}: MobileNavProps) => {
    const sidebarContext = useSidebar()
    // Use context if available, otherwise use props
    const open =
        sidebarContext.open !== undefined
            ? sidebarContext.open
            : propIsOpen ?? false
    const toggleSidebar =
        sidebarContext.toggleSidebar || propOnToggle || (() => {})

    return (
        <div
            className={cn(
                'fixed top-0 left-0 right-0 z-[15] flex items-center py-3 px-4 transition-[margin-left] duration-300 ease-in-out',
                open ? 'ml-[260px] w-screen' : 'ml-0 w-full',
                isTopNavVisible
                    ? 'bg-white dark:bg-gray-900 border-b-[3px] border-[#333333] dark:border-gray-700'
                    : 'bg-transparent'
            )}
        >
            <button
                onClick={toggleSidebar}
                className="h-8 w-8 flex items-center justify-center cursor-pointer"
                aria-label={open ? 'close menu' : 'open menu'}
            >
                {open ? (
                    <DrawerActive className="h-8 w-8" />
                ) : (
                    <DrawerInactive className="h-8 w-8" />
                )}
            </button>

            {isTopNavVisible && pageTitle && (
                <div className="flex flex-1 justify-center">
                    <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                        {pageTitle.split(' ')[1] || pageTitle}
                    </h2>
                </div>
            )}

            <div className="h-6 w-6" />
        </div>
    )
}

export default MobileNav
